import { pxToRem, responsiveFontSizes } from "../utils/getFontValue";

declare module "@mui/material/styles" {
    interface TypographyVariants {
        body3: React.CSSProperties;
        section: React.CSSProperties;
        inputLabel: React.CSSProperties;
        tableOverline: React.CSSProperties;
        helper: React.CSSProperties;
        small: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        body3?: React.CSSProperties;
        section?: React.CSSProperties;
        inputLabel?: React.CSSProperties;
        tableOverline?: React.CSSProperties;
        helper?: React.CSSProperties;
        small?: React.CSSProperties;
    }
}

declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        body3: true;
        section: true;
        inputLabel: true;
        tableOverline: true;
        helper: true;
        small: true;
    }
}

declare module "@mui/material/styles/createTypography" {
    interface FontStyle {
        fontFamilyHeading: string;
    }
}

const FONT_PRIMARY = "Merriweather, serif";
const FONT_SECONDARY = "IBM Plex Sans, sans-serif";

const typography = {
    fontFamily: FONT_SECONDARY,
    fontFamilyHeading: FONT_PRIMARY,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
        fontFamily: FONT_PRIMARY,
        fontWeight: 900,
        lineHeight: 80 / 64,
        fontSize: pxToRem(40),
        letterSpacing: 2,
        ...responsiveFontSizes({ sm: 40, md: 40, lg: 40 }),
    },
    h2: {
        fontFamily: FONT_PRIMARY,
        fontWeight: 900,
        lineHeight: 64 / 48,
        fontSize: pxToRem(32),
        ...responsiveFontSizes({ sm: 32, md: 32, lg: 32 }),
    },
    h3: {
        fontFamily: FONT_PRIMARY,
        fontWeight: 900,
        lineHeight: 1.5,
        fontSize: pxToRem(24),
        ...responsiveFontSizes({ sm: 24, md: 24, lg: 24 }),
    },
    h4: {
        fontFamily: FONT_PRIMARY,
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(20),
        ...responsiveFontSizes({ sm: 20, md: 20, lg: 20 }),
    },
    h5: {
        fontFamily: FONT_PRIMARY,
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(18),
        ...responsiveFontSizes({ sm: 18, md: 18, lg: 18 }),
    },
    h6: {
        fontFamily: FONT_PRIMARY,
        fontWeight: 700,
        lineHeight: 28 / 18,
        fontSize: pxToRem(17),
        ...responsiveFontSizes({ sm: 17, md: 17, lg: 17 }),
    },
    subtitle1: {
        fontWeight: 400,
        lineHeight: 1.5,
        fontSize: pxToRem(16),
    },
    subtitle2: {
        fontWeight: 400,
        lineHeight: 22 / 14,
        fontSize: pxToRem(14),
    },
    body1: {
        lineHeight: 1.5,
        fontSize: pxToRem(16),
    },
    body2: {
        lineHeight: 22 / 14,
        fontSize: pxToRem(14),
    },
    body3: {
        lineHeight: 22 / 12,
        fontSize: pxToRem(12),
    },
    caption: {
        lineHeight: 1.5,
        fontSize: pxToRem(12),
    },
    overline: {
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(12),
        textTransform: "uppercase",
    },
    button: {
        fontWeight: 700,
        lineHeight: 24 / 14,
        fontSize: pxToRem(14),
        // textTransform: "capitalize",
        textTransform: "none",
    },
    section: {
        letterSpacing: 1.1,
        lineHeight: 1.5,
        fontSize: pxToRem(12),
        fontWeight: 400,
        textTransform: "uppercase",
    },
    inputLabel: {
        lineHeight: 1.5,
        fontSize: pxToRem(14),
        fontWeight: 500,
    },
    tableOverline: {
        fontWeight: 600,
        lineHeight: 1.2,
        fontSize: pxToRem(12),
    },
    helper: {
        fontWeight: 400,
        lineHeight: 1.5,
        fontSize: pxToRem(12),
        letterSpacing: 1.1,
    },
    small: {
        fontWeight: 400,
        lineHeight: 1,
        fontSize: pxToRem(10),
    },
} as const;

export default typography;
